import { Avatar, AvatarGroup, Box, Slider } from "@mui/material";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { formatFileSize } from "../../services/trackService";
import { ArtWork } from "../../components/TrackComponents/ArtWork/ArtWork";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { APP_COLOR, TEXT_COLOR } from "../../theme";
import PauseIcon from "@mui/icons-material/Pause";
import { handleCreateGroupChat } from "../../components/TrackComponents/TrackHandler/trackHandlers";
import { downloadSong } from "../../components/TrackComponents/TrackHandler/trackHandlers";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { AudioFileDetailsProps } from "./types";
import { MainContainer, styles } from "./styles";
import MessageIcon from "@mui/icons-material/Message";
import { FileTrackLinkingModal } from "../Modal/FileTrackLinkingModal";
import LinkIcon from "@mui/icons-material/Link";

export const AudioFileDetails: React.FC<AudioFileDetailsProps> = ({
  fileDetails,
  user,
  fileSize,
  createdDate,
  setOpenCollabsModal,
  handleOpenDeleteModal,
  talkJsContext,
  setCreateGroupChatClicked,
}) => {
  const [audioTrack, setAudioTrack] = useState<WaveSurfer>();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playingTime, setPlayingTime] = useState<number>(0);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [intervalTimeout, setIntervalTimeOut] = useState<any>();
  const [isFileTrackLinkingModalOpen, setIsFileTrackLinkingModalOpen] =
    useState<boolean>(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const handlePlayPause = () => {
    if (audioTrack?.isPlaying()) {
      setIsPlaying(false);
      audioTrack.pause();
      clearInterval(intervalTimeout);
    } else {
      setIsPlaying(true);
      audioTrack?.play();
      const timeOut: any = setInterval(() => {
        if (audioTrack) {
          const seekPosition = audioTrack.getCurrentTime();
          setPlayingTime(seekPosition);
        }
        setIntervalTimeOut(timeOut);
      }, 1000);
    }
  };

  const createGroupChat = (
    id: string | undefined,
    title: string | undefined
  ) => {
    handleCreateGroupChat(
      talkJsContext,
      fileDetails.getCollaboratorsList(),
      navigate,
      setCreateGroupChatClicked,
      id,
      title
    );
  };

  const timeString = (timeStamp: number) => {
    const minutes = Math.floor(timeStamp / 60);
    const seconds = Math.ceil(timeStamp % 60);
    const formattedTime =
      seconds >= 10 ? minutes + ":" + seconds : minutes + ":0" + seconds;
    return formattedTime;
  };

  const handleSliderChange = (event: any, newValue: any) => {
    if (setPlayingTime) setPlayingTime(newValue);
    if (audioTrack) audioTrack.setCurrentTime(newValue);
  };

  useEffect(() => {
    if (ref.current && fileDetails?.getFileUrl()) {
      const audioTrack = WaveSurfer.create({
        container: ref.current,
        responsive: true,
        cursorWidth: 0,
        barWidth: 4,
        barRadius: 5,
        progressColor: APP_COLOR,
      });
      setAudioTrack(audioTrack);
      const audio = new Audio(fileDetails.getFileUrl());
      audio &&
        audio.addEventListener("loadedmetadata", () => {
          const duration = audio.duration;
          setAudioDuration(duration);
        });
      audioTrack.load(fileDetails?.getFileUrl());
    }
  }, [fileDetails]);

  useEffect(() => {
    return () => {
      audioTrack?.pause();
    };
  }, [audioTrack]);

  return (
    <>
      {isFileTrackLinkingModalOpen && (
        <FileTrackLinkingModal
          isFileTrackLinkingModalOpen={isFileTrackLinkingModalOpen}
          setIsFileTrackLinkingModalOpen={setIsFileTrackLinkingModalOpen}
          file={fileDetails}
        />
      )}
      <MainContainer>
        <ArtWork expand={false} image={"/src/ts/app/public/logo512.png"} />
        <Box sx={styles.detailsContainer}>
          <Typography sx={styles.text}>{fileDetails?.getFilename()}</Typography>
          <Box sx={styles.typographyContainer}>
            <Typography sx={styles.text}>Owner:</Typography>
            <Typography>{user && user.getUsername()}</Typography>
          </Box>
          <Box sx={styles.typographyContainer}>
            <Typography sx={styles.text}>Label:</Typography>
            <Typography>{fileDetails?.getLabel()}</Typography>
          </Box>
          <Box sx={styles.typographyContainer}>
            <Typography sx={styles.text}>file Size:</Typography>
            <Typography>{fileSize ? formatFileSize(fileSize) : "-"}</Typography>
          </Box>
          <Box sx={styles.typographyContainer}>
            <Typography sx={styles.text}>Created Date:</Typography>
            <Typography>{createdDate}</Typography>
          </Box>
          <Box sx={styles.typographyContainer}>
            <Typography sx={styles.text}>BPM:</Typography>
            <Typography>{fileDetails.getAudioMetadata()?.getBpm()}</Typography>
          </Box>
          <Box sx={styles.typographyContainer}>
            <AvatarGroup sx={{ display: "flex", gap: "5px" }}>
              {fileDetails?.getCollaboratorsList() &&
                fileDetails
                  ?.getCollaboratorsList()
                  .filter((c: any) => c.getUsername())
                  .map((contributor: any) => {
                    return (
                      <Tooltip
                        key={contributor.getUsername()}
                        title={contributor.getUsername()}
                        placement="bottom"
                      >
                        <Avatar
                          style={styles.avatar}
                          src={
                            contributor.getProfilePicture()
                              ? contributor.getProfilePicture()
                              : "/broken-image.jpg"
                          }
                          onClickCapture={() => {
                            navigate(
                              `/user-profile/${contributor.getUsername()}`
                            );
                          }}
                          alt={contributor.getUsername()}
                        />
                      </Tooltip>
                    );
                  })}
            </AvatarGroup>
          </Box>
        </Box>
        <Box sx={styles.buttonsMainContainer}>
          <Box sx={styles.buttonContainer}>
            <IconButton
              sx={{ paddingTop: "11px", color: "white" }}
              onClick={handlePlayPause}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton
              sx={{ paddingTop: "11px", color: "white" }}
              aria-label="download"
              onClick={() =>
                downloadSong(fileDetails.getFileUrl(), fileDetails.getLabel())
              }
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setOpenCollabsModal(true);
              }}
            >
              <ManageAccountsIcon />
            </IconButton>
            <Tooltip title="Link File With Tracks" placement="bottom">
              <IconButton
                sx={{ color: TEXT_COLOR }}
                onClick={() => setIsFileTrackLinkingModalOpen(true)}
              >
                <LinkIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              sx={{ color: TEXT_COLOR }}
              aria-label="edit"
              onClick={() => {
                createGroupChat(fileDetails.getId(), fileDetails.getLabel());
              }}
            >
              <MessageIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              onClick={() => {
                handleOpenDeleteModal(fileDetails.getId());
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          <Box ref={ref}></Box>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
            <Box sx={{ marginRight: "15px" }}>
              {playingTime !== 0 ? timeString(playingTime) : "00:00"}
            </Box>
            <Slider
              value={Math.round(playingTime)}
              aria-label="Default"
              valueLabelDisplay="auto"
              onChange={handleSliderChange}
              min={0}
              sx={{ width: "90%" }}
              max={audioDuration}
            />
            {audioDuration && (
              <Box sx={{ marginLeft: "15px" }}>
                {audioDuration !== 0 ? timeString(audioDuration) : "00:00"}
              </Box>
            )}
          </Box>
        </Box>
      </MainContainer>
    </>
  );
};
